/*
 * @Author: AI Assistant
 * @Date: 2024-12-30 16:00:00
 * @LastEditors: AI Assistant
 * @LastEditTime: 2024-12-30 16:00:00
 * @FilePath: /RetailApp/retail-global-h5/src/pages/CPS/components/LogisticsTrackModal.tsx
 * @Description: 物流追踪弹窗组件 - 根据Figma设计稿实现
 */
import { memo, useState, useEffect, useRef } from 'react';
import intl from 'react-intl-universal';
import { ExtendedWaybillInfo, queryCPSLogistics, CPSLogisticsInfo, LogisticsTrackNode, LogisticsExtendInfo } from '../../../../api/retailGlobal/CPS';
import closeIcon from '../../../../assets/retailGlobal/CPS/close-icon.png';
import styles from './LogisticsTrackModal.less';
import Toast from './Toast';

interface LogisticsTrackModalProps {
  order: ExtendedWaybillInfo;
  orderItemId?: string;
  onClose: () => void;
  urlParams?: {
    saOrderId?: string;
    idcRegion?: string;
    timeStamp?: string;
    nonce?: string;
    sign?: string;
  };
}

function LogisticsTrackModal({ order, orderItemId, onClose, urlParams }: LogisticsTrackModalProps) {
  const [logisticsInfo, setLogisticsInfo] = useState<CPSLogisticsInfo | null>(null);
  const [loading, setLoading] = useState(true);
  
  // 使用 ref 保存最新的 order 和 urlParams，避免 useEffect 依赖它们
  const orderRef = useRef(order);
  const urlParamsRef = useRef(urlParams);
  
  // 更新 ref 的值
  orderRef.current = order;
  urlParamsRef.current = urlParams;

  // 防止滚动穿透：弹窗打开时禁用背景滚动
  useEffect(() => {
    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = 'hidden';
    
    return () => {
      document.body.style.overflow = originalStyle;
    };
  }, []);

  // 阻止弹窗 overlay 的滚动事件穿透
  const handleOverlayTouchMove = (e: React.TouchEvent) => {
    e.preventDefault();
  };

  // 阻止弹窗内容区域的滚动事件冒泡
  const handleContentTouchMove = (e: React.TouchEvent) => {
    e.stopPropagation();
  };

  // 只依赖 orderItemId 变化时请求接口
  useEffect(() => {
    const fetchLogisticsData = async () => {
      const currentOrder = orderRef.current;
      const currentUrlParams = urlParamsRef.current;
      
      if (!currentOrder.waybillItem || currentOrder.waybillItem.length === 0) {
        setLoading(false);
        return;
      }

      if (!currentUrlParams?.saOrderId || !currentUrlParams?.idcRegion || !currentUrlParams?.timeStamp || !currentUrlParams?.nonce || !currentUrlParams?.sign) {
        setLoading(false);
        return;
      }

      // 根据传入的 orderItemId 查找对应的 waybillItem，如果没有传入则使用第一个
      const waybillItem = orderItemId 
        ? currentOrder.waybillItem?.find(item => String(item.orderItemId) === String(orderItemId))
        : currentOrder.waybillItem?.[0];
      
      if (!waybillItem) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        const response = await queryCPSLogistics([{
          saOrderId: currentUrlParams.saOrderId,
          waybillId: waybillItem.waybillId,
          orderItemId: String(waybillItem.orderItemId),
          itemDeliveryId: String(waybillItem.itemDeliveryId),
          phoneLast4: currentOrder.phoneLast4,
          idcRegion: currentUrlParams.idcRegion,
          timeStamp: Number(currentUrlParams.timeStamp),
          nonce: currentUrlParams.nonce,
          sign: currentUrlParams.sign
        }]);

        if (response.code === 0 && response.data.trackList) {
          const trackList = response.data.trackList;

          setLogisticsInfo({
            trackList
          });
        } else {
          Toast.error(response.message);
        }
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };

    fetchLogisticsData();
  }, [orderItemId]);

  // 直接使用接口数据
  const trackList = logisticsInfo?.trackList || [];

  if (loading) {
    return (
      <div 
        className={styles['modal-overlay']} 
        onClick={onClose}
        onTouchMove={handleOverlayTouchMove}
      >
        <div className={styles['modal-content']} onClick={(e) => e.stopPropagation()}>
          <div className={styles['modal-header']}>
            <div className={styles['header-title']}>
              {intl.get('cps.logistics.title')}
            </div>
            <div className={styles['close-btn']} onClick={onClose}>
              <img src={closeIcon} alt="close" />
            </div>
          </div>
          <div style={{ padding: '20px', textAlign: 'center' }}>
            {intl.get('cps.querying')}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={styles['modal-overlay']} 
      onClick={onClose}
      onTouchMove={handleOverlayTouchMove}
    >
      <div className={styles['modal-content']} onClick={(e) => e.stopPropagation()}>
        <div className={styles['modal-header']}>
          <div className={styles['header-title']}>
            {intl.get('cps.logistics.title')}
          </div>
          <div className={styles['close-btn']} onClick={onClose}>
            <img src={closeIcon} alt="close" />
          </div>
        </div>

        <div 
          className={styles['timeline-section']}
          onTouchMove={handleContentTouchMove}
        >
          {trackList.map((node: LogisticsTrackNode, index: number) => {
            return (
              <div key={index} className={`${styles['timeline-item']} ${index === 0 ? styles['timeline-item-first'] : ''}`}>
                <div className={index === 0 ? styles['timeline-dot-current'] : styles['timeline-dot']}></div>
                <div className={styles['timeline-content']}>
                  <div className={index === 0 ? styles['timeline-title-current'] : styles['timeline-title']}>
                    {node.action || ''}
                  </div>
                  <div className={styles['timeline-description']}>
                    {node.extendInfos && node.extendInfos.length > 0 && (
                      <>
                        {node.extendInfos.map((info: LogisticsExtendInfo, infoIndex: number) => {
                          // 如果是日志信息
                          if (info.logInfos && Array.isArray(info.logInfos)) {
                            return (
                              <div key={infoIndex} className={styles['extend-info-item']}>
                                <div className={styles['log-details']}>
                                  {info.logInfos.map((log, logIndex: number) => (
                                    <div key={logIndex} className={styles['log-item']}>
                                      <span className={styles['log-name']}>{log.logName}</span>
                                      {' '}
                                      <span className={styles['log-time']}>{log.logValue}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          }
                          // 如果有 keyName 和 keyValue（如运单号）
                          if (info.keyName && info.keyValue) {
                            return (
                              <div key={infoIndex} className={styles['extend-info-item']}>
                                {info.keyName}: {info.keyValue}
                              </div>
                            );
                          }
                          // 如果只有 keyName（描述性文本）
                          if (info.keyName) {
                            return (
                              <div key={infoIndex} className={styles['extend-info-item']}>
                                {info.keyName}
                              </div>
                            );
                          }
                          return null;
                        })}
                      </>
                    )}
                  </div>
                  <div className={styles['timeline-time']}>
                    {node.operatorTime || ''}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className={styles['modal-footer']}>
          <button className={styles['confirm-btn']} onClick={onClose}>
            {intl.get('cps.confirm') || 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
}

// 只在 orderItemId 变化时重新渲染组件
export default memo(LogisticsTrackModal, (prevProps, nextProps) => {
  return prevProps.orderItemId === nextProps.orderItemId;
});
